import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  increment,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config';
import { categoriesCollection, wallpapersCollection, type Category } from '../schema';
import { adminService } from './adminService';



export class CategoryService {
  /**
   * Create a new category (admin only)
   */
  async createCategory(name: string, description = ''): Promise<Category> {
    try {
      await this.checkAdminPermissions();

      if (!name?.trim()) {
        throw new Error('Category name is required');
      }

      const categoryName = name.trim();
      const normalizedCategoryName = categoryName.toLowerCase();

      // Check if category already exists
      const q = query(
        categoriesCollection,
        where('nameLower', '==', normalizedCategoryName)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        throw new Error('Category already exists');
      }

      const now = new Date();
      const categoryRef = doc(categoriesCollection);
      
      const categoryData: Omit<Category, 'id'> = {
        name: categoryName,
        nameLower: normalizedCategoryName,
        description: description.trim(),
        wallpaperCount: 0,
        createdAt: now,
        updatedAt: now
      };

      await setDoc(categoryRef, categoryData);

      return {
        id: categoryRef.id,
        ...categoryData
      };
    } catch (error) {
      console.error('Error creating category:', error);
      throw error instanceof Error ? error : new Error('Failed to create category');
    }
  }

  /**
   * Get all categories with optional wallpaper counts
   * @param includeWallpaperCounts Whether to include wallpaper counts (default: false)
   * @returns Array of categories
   */

  /**
   * Get a single category by ID
   */
  async getCategory(id: string): Promise<Category> {
    try {
      const docRef = doc(categoriesCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Category not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Category;
    } catch (error) {
      console.error('Error getting category:', error);
      throw error instanceof Error ? error : new Error('Failed to get category');
    }
  }

  /**
   * Update a category
   */
  async updateCategory(
    id: string,
    updates: Partial<Omit<Category, 'id' | 'createdAt' | 'wallpaperCount'>>
  ): Promise<void> {
    try {
      await this.checkAdminPermissions();

      const updateData: Record<string, any> = {
        ...updates,
        updatedAt: new Date()
      };

      // If name is being updated, update the lowercase version too
      if (updates.name) {
        updateData.nameLower = updates.name.toLowerCase();
      }

      const categoryRef = doc(categoriesCollection, id);
      await updateDoc(categoryRef, updateData);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error instanceof Error ? error : new Error('Failed to update category');
    }
  }

  /**
   * Delete a category (admin only)
   */
  async deleteCategory(id: string): Promise<void> {
    try {
      await this.checkAdminPermissions();

      // Get the category first to get its name for updating wallpapers
      const category = await this.getCategory(id);
      if (!category) {
        throw new Error('Category not found');
      }

      // Start a batch to perform multiple operations atomically
      const batch = writeBatch(db);
      
      // Delete the category
      const categoryRef = doc(categoriesCollection, id);
      batch.delete(categoryRef);

      // Update all wallpapers in this category to 'Uncategorized'
      const wallpapersQuery = query(
        wallpapersCollection,
        where('category', '==', category.name)
      );
      
      const wallpapersSnapshot = await getDocs(wallpapersQuery);
      wallpapersSnapshot.forEach((doc) => {
        batch.update(doc.ref, { category: 'Uncategorized' });
      });
      
      // Commit the batch
      await batch.commit();
      
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error instanceof Error ? error : new Error('Failed to delete category');
    }
  }

  /**
   * Delete category by name (admin only)
   * This is a convenience method that first looks up the category by name
   * and then calls deleteCategory with the ID
   */
  async deleteCategoryByName(categoryName: string): Promise<void> {
    try {
      if (!categoryName.trim()) {
        throw new Error('Category name is required');
      }

      const q = query(categoriesCollection, where('nameLower', '==', categoryName.trim().toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('Category not found');
      }

      const categoryDoc = querySnapshot.docs[0];
      await this.deleteCategory(categoryDoc.id);
      
    } catch (error) {
      console.error('Error deleting category by name:', error);
      throw error instanceof Error ? error : new Error('Failed to delete category by name');
    }
  }

  /**
   * Get all categories with wallpaper counts
   */
  async getAllCategories(includeWallpaperCounts = false): Promise<Category[]> {
    try {
      const q = query(categoriesCollection, orderBy('nameLower'));
      const querySnapshot = await getDocs(q);
      
      if (!includeWallpaperCounts) {
        const categories = querySnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Simple function to convert Firestore timestamp to Date
          const toDate = (value: unknown): Date => {
            try {
              if (!value) return new Date();
              if (value instanceof Date) return value;
              
              // Handle Firestore Timestamp
              if (value && typeof value === 'object' && 'toDate' in value) {
                const timestamp = value as { toDate: () => Date };
                return timestamp.toDate();
              }
              
              // Handle timestamp in seconds
              const timestamp = Number(value);
              if (!isNaN(timestamp)) {
                return new Date(timestamp * 1000);
              }
              
              return new Date(String(value));
            } catch (error) {
              console.warn('Error converting timestamp to Date:', error);
              return new Date();
            }
          };

          // Process the document data
          const processedData: Omit<Category, 'id'> = {
            name: String(data.name || ''),
            nameLower: String(data.nameLower || '').toLowerCase(),
            description: String(data.description || ''),
            wallpaperCount: Number(data.wallpaperCount) || 0,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt)
          };
          
          return {
            id: doc.id,
            ...processedData
          } as Category;
        });
        
        return categories;
      }
      
      // If we need wallpaper counts, fetch wallpapers and calculate
      const wallpapersSnapshot = await getDocs(wallpapersCollection);
      const categoryCounts = new Map<string, number>();
      
      wallpapersSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.category) {
          categoryCounts.set(data.category, (categoryCounts.get(data.category) || 0) + 1);
        }
      });
      
      // Process categories with counts
      const categories = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          wallpaperCount: categoryCounts.get(data.name) || 0
        } as Category;
      });
      
      // Add uncategorized count if there are any
      const uncategorizedCount = wallpapersSnapshot.docs
        .filter(doc => !doc.data().category)
        .length;

      if (uncategorizedCount > 0) {
        categories.push({
          id: 'uncategorized',
          name: 'Uncategorized',
          nameLower: 'uncategorized',
          description: 'Wallpapers without a category',
          wallpaperCount: uncategorizedCount,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      // Sort categories alphabetically, but put "Uncategorized" last
      return categories.sort((a, b) => {
        if (a.name === 'Uncategorized') return 1;
        if (b.name === 'Uncategorized') return -1;
        return a.name.localeCompare(b.name);
      });
      
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error instanceof Error ? error : new Error('Failed to get categories');
    }
  }

  /**
   * Get category statistics (admin only)
   */
  async getCategoryStats(): Promise<{
    totalCategories: number;
    averageWallpapersPerCategory: number;
    largestCategory: { name: string; count: number };
    smallestCategory: { name: string; count: number };
  }> {
    try {
      // Check admin permissions
      await this.checkAdminPermissions();

      const categories = await this.getAllCategories();
      
      if (categories.length === 0) {
        return {
          totalCategories: 0,
          averageWallpapersPerCategory: 0,
          largestCategory: { name: '', count: 0 },
          smallestCategory: { name: '', count: 0 }
        };
      }

      const totalWallpapers = categories.reduce((sum, cat) => sum + cat.wallpaperCount, 0);
      const averageWallpapersPerCategory = Math.round(totalWallpapers / categories.length);

      const sortedByCount = [...categories].sort((a, b) => b.wallpaperCount - a.wallpaperCount);
      const largestCategory = { name: sortedByCount[0].name, count: sortedByCount[0].wallpaperCount };
      const smallestCategory = { name: sortedByCount[sortedByCount.length - 1].name, count: sortedByCount[sortedByCount.length - 1].wallpaperCount };

      return {
        totalCategories: categories.length,
        averageWallpapersPerCategory,
        largestCategory,
        smallestCategory
      };

    } catch (error) {
      console.error('Error getting category stats:', error);
      throw error instanceof Error ? error : new Error('Failed to get category statistics');
    }
  }

  /**
   * Check if current user has admin permissions
   */
  private async checkAdminPermissions(): Promise<void> {
    const isAdmin = await adminService.isCurrentUserAdmin();
    if (!isAdmin) {
      throw new Error('Admin privileges required');
    }
  }

  /**
   * Increment the wallpaper count for a category
   */
  async incrementWallpaperCount(categoryId: string): Promise<void> {
    try {
      const categoryRef = doc(categoriesCollection, categoryId);
      await updateDoc(categoryRef, {
        wallpaperCount: increment(1),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error incrementing wallpaper count:', error);
      throw error;
    }
  }

  /**
   * Decrement the wallpaper count for a category
   */
  async decrementWallpaperCount(categoryId: string): Promise<void> {
    try {
      const categoryRef = doc(categoriesCollection, categoryId);
      
      // Check if the category exists before trying to update it
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        console.warn(`Category ${categoryId} does not exist, skipping count decrement`);
        return;
      }
      
      await updateDoc(categoryRef, {
        wallpaperCount: increment(-1),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error decrementing wallpaper count:', error);
      throw error;
    }
  }
}

export const categoryService = new CategoryService();